import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navLinks } from '../data/mock';
import { Menu, X } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    gsap.fromTo(navRef.current,
      { y: -100, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, ease: 'power3.out', delay: 0.5 }
    );
  }, []);

  // Scroll to top when navigating to a new page
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    
    // If it's a page link (like /about)
    if (href.startsWith('/') && !href.includes('#')) {
      navigate(href);
      window.scrollTo(0, 0);
      return;
    }
    
    // If it's a hash link on same page or from home
    if (href.includes('#')) {
      const [path, hash] = href.split('#');
      const targetId = hash;
      
      // If we're on a different page, navigate first
      if (path && path !== '/' && location.pathname !== '/') {
        navigate(path);
        setTimeout(() => {
          const element = document.getElementById(targetId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else if (path === '/' && location.pathname !== '/') {
        // Navigate to home first, then scroll
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(targetId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        // Same page, just scroll
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  const renderNavLink = (link) => {
    // Check if it's an external page link (starts with / but no hash)
    if (link.href.startsWith('/') && !link.href.includes('#')) {
      return (
        <Link
          key={link.name}
          to={link.href}
          onClick={() => window.scrollTo(0, 0)}
          className="text-white/80 hover:text-white font-medium text-sm uppercase tracking-wider animated-line pb-1 transition-colors"
        >
          {link.name}
        </Link>
      );
    }
    // Hash link (same page or home page section)
    return (
      <a
        key={link.name}
        href={link.href}
        onClick={(e) => handleNavClick(e, link.href)}
        className="text-white/80 hover:text-white font-medium text-sm uppercase tracking-wider animated-line pb-1 transition-colors"
      >
        {link.name}
      </a>
    );
  };

  const renderMobileNavLink = (link) => {
    if (link.href.startsWith('/') && !link.href.includes('#')) {
      return (
        <Link
          key={link.name}
          to={link.href}
          className="text-white/80 hover:text-white font-medium text-lg uppercase tracking-wider transition-colors"
          onClick={() => {
            setIsMobileMenuOpen(false);
            window.scrollTo(0, 0);
          }}
        >
          {link.name}
        </Link>
      );
    }
    return (
      <a
        key={link.name}
        href={link.href}
        className="text-white/80 hover:text-white font-medium text-lg uppercase tracking-wider transition-colors"
        onClick={(e) => {
          handleNavClick(e, link.href);
          setIsMobileMenuOpen(false);
        }}
      >
        {link.name}
      </a>
    );
  };

  return (
    <>
      {/* Skip Navigation Link for Accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[99999] focus:px-4 focus:py-2 focus:bg-[#D3FF62] focus:text-[#004534] focus:rounded-lg focus:font-semibold focus:text-sm"
        data-testid="skip-nav-link"
      >
        Skip to main content
      </a>
      <motion.header
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-500 ${
        isScrolled ? 'py-3' : 'py-6'
      }`}
    >
      <div className="container">
        <nav
          aria-label="Main navigation"
          className={`flex items-center justify-between px-6 py-3 rounded-full transition-all duration-500 ${
            isScrolled
              ? 'bg-[#0A0A0A]/90 backdrop-blur-xl border border-white/10'
              : 'bg-transparent'
          }`}
        >
          {/* Logo */}
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center group">
            {/* Full logo for larger screens */}
            <img 
              src="/logo-full.png" 
              alt="OmniSportsGroup" 
              className="hidden sm:block h-6 lg:h-8 xl:h-9 w-auto transition-transform group-hover:scale-105"
            />
            {/* Compact logo for mobile */}
            <img 
              src="/logo-compact.png" 
              alt="OSG" 
              className="sm:hidden h-8 w-auto transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation - Only show on large screens */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => renderNavLink(link))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link 
              to="/demo" 
              className="btn-primary text-sm"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile/Tablet Menu Button */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile/Tablet Menu */}
        <motion.div
          initial={false}
          animate={isMobileMenuOpen ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
          className="lg:hidden overflow-hidden bg-[#0A0A0A]/95 backdrop-blur-xl rounded-2xl mt-2 border border-white/10"
        >
          <div className="p-6 flex flex-col gap-4">
            {navLinks.map((link) => renderMobileNavLink(link))}
            <Link 
              to="/demo" 
              className="btn-primary text-center mt-4" 
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Get Started
            </Link>
          </div>
        </motion.div>
      </div>
    </motion.header>
    </>
  );
};

export default Navbar;
